import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../lib/api";
import { AccountsOverview } from "../components/accounts/AccountsOverview";
import { BankingTab } from "../components/accounts/BankingTab";
import { TransactionsTab } from "../components/accounts/TransactionsTab";
import { ReportsTab } from "../components/accounts/ReportsTab";
import { AccountsSettings } from "../components/accounts/AccountsSettings";
import { VatReturnsTab } from "../components/accounts/VatReturnsTab";
import { PayrollTab } from "../components/accounts/PayrollTab";
import { ReceiptsTab } from "../components/accounts/ReceiptsTab";
import { BillsTab } from "../components/accounts/BillsTab";
import { Calculator } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { hasFeature } from "../lib/features";

// Payroll is part of the HR & Payroll add-on; everything else here is Accounts & Bookkeeping
const TABS = [["overview", "Overview"], ["banking", "Banking"], ["transactions", "Transactions"], ["bills", "Bills"], ["receipts", "Receipts"], ["reports", "Reports"], ["vat", "VAT returns"], ["payroll", "Payroll", "hr"], ["settings", "Settings"]];

/** Accounts hub — bookkeeping, bank feeds, explaining bank lines, reports and VAT settings (FreeAgent-style). */
export default function AccountsPage() {
  const { tab = "overview" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const hasAccounts = hasFeature(user, "accounts");
  const tabs = TABS.filter(([k, , feat]) => (feat ? hasFeature(user, feat) : hasAccounts));
  const load = () => API.get("/accounts/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  useEffect(() => { if (hasAccounts) load(); }, [hasAccounts]);
  const active = tabs.some(([k]) => k === tab) ? tab : (tabs[0]?.[0] || "overview");
  return (
    <div className="space-y-5" data-testid="accounts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Chivo] inline-flex items-center gap-2"><Calculator size={26} className="text-primary" /> Accounts</h1>
          <p className="text-sm text-muted-foreground">Bookkeeping, bank feeds, VAT returns, payroll and HMRC filing — the whole accounts side of the business in one place.</p>
        </div>
        {settings?.settings?.vat_registered && settings?.current_vat_period && (
          <div className="rounded-md border border-border bg-card px-3 py-2 text-xs" data-testid="accounts-vat-period-chip">
            <span className="text-muted-foreground">VAT period</span> <span className="font-medium">{settings.current_vat_period.label}</span> <span className="text-muted-foreground">· file by</span> <span className="font-medium">{new Date(settings.current_vat_period.due).toLocaleDateString("en-GB")}</span>
          </div>
        )}
      </div>
      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="accounts-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" onClick={() => navigate(k === "overview" ? "/accounts" : `/accounts/${k}`)} className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${active === k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`accounts-tab-${k}`}>{l}</button>
        ))}
      </div>
      {active === "overview" && <AccountsOverview onGo={(k) => navigate(`/accounts/${k}`)} />}
      {active === "banking" && <BankingTab />}
      {active === "transactions" && <TransactionsTab />}
      {active === "bills" && <BillsTab />}
      {active === "reports" && <ReportsTab />}
      {active === "receipts" && <ReceiptsTab />}
      {active === "vat" && <VatReturnsTab />}
      {active === "payroll" && <PayrollTab />}
      {active === "settings" && <AccountsSettings onSaved={load} />}
    </div>
  );
}
